import React, { useState, useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, ReferenceLine
} from 'recharts';
import Icon from 'components/AppIcon';
import { format, startOfWeek, startOfMonth } from 'date-fns';

const METAL_COLORS = ['#059669', '#2563eb', '#7c3aed', '#d97706', '#0891b2', '#be185d', '#dc2626', '#65a30d', '#ea580c'];

function groupByPeriod(sales, period) {
  const groups = {};
  sales?.forEach(s => {
    if (!s?.sold_at) return;
    const d = new Date(s?.sold_at);
    const key = period === 'week'
      ? format(startOfWeek(d, { weekStartsOn: 1 }), 'dd/MM')
      : format(startOfMonth(d), 'MM/yy');
    if (!groups?.[key]) groups[key] = { period: key, margins: [], total: 0, count: 0 };
    const entryPrice = parseFloat(s?.purchase_value) / Math.max(parseFloat(s?.weight_t), 0.001);
    const marginPerTon = parseFloat(s?.sale_price_per_ton) - entryPrice;
    groups?.[key]?.margins?.push(marginPerTon);
    groups[key].total += marginPerTon;
    groups[key].count += 1;
  });
  return Object.values(groups)
    ?.map(g => ({ period: g?.period, avgMargin: g?.count > 0 ? g?.total / g?.count : 0 }))
    ?.sort((a, b) => a?.period?.localeCompare(b?.period));
}

function groupByMetal(sales) {
  const groups = {};
  sales?.forEach(s => {
    const metal = s?.metal_name || 'Desconocido';
    if (!groups?.[metal]) groups[metal] = { metal, total: 0, count: 0 };
    const entryPrice = parseFloat(s?.purchase_value) / Math.max(parseFloat(s?.weight_t), 0.001);
    const marginPerTon = parseFloat(s?.sale_price_per_ton) - entryPrice;
    groups[metal].total += marginPerTon;
    groups[metal].count += 1;
  });
  return Object.values(groups)
    ?.map(g => ({ metal: g?.metal, avgMargin: g?.count > 0 ? g?.total / g?.count : 0 }))
    ?.sort((a, b) => b?.avgMargin - a?.avgMargin);
}

function groupByCategory(sales) {
  const groups = {};
  sales?.forEach(s => {
    const cat = s?.category_name || 'Desconocido';
    if (!groups?.[cat]) groups[cat] = { category: cat, total: 0, count: 0 };
    const entryPrice = parseFloat(s?.purchase_value) / Math.max(parseFloat(s?.weight_t), 0.001);
    const marginPerTon = parseFloat(s?.sale_price_per_ton) - entryPrice;
    groups[cat].total += marginPerTon;
    groups[cat].count += 1;
  });
  return Object.values(groups)
    ?.map(g => ({ category: g?.category, avgMargin: g?.count > 0 ? g?.total / g?.count : 0 }))
    ?.sort((a, b) => b?.avgMargin - a?.avgMargin)
    ?.slice(0, 10);
}

const CustomTooltip = ({ active, payload, label, unit = '€/t', drillable }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-2.5 shadow-lg" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      {payload?.map((p, i) => (
        <p key={i} className="text-xs font-semibold" style={{ color: p?.color }}>
          {p?.name}: {typeof p?.value === 'number' ? p?.value?.toLocaleString('es-ES', { maximumFractionDigits: 1 }) : p?.value} {unit}
        </p>
      ))}
      {drillable && <p className="text-xs text-muted-foreground mt-1 italic">Clic para filtrar ventas</p>}
    </div>
  );
};

export default function MarginCharts({ sales, loading, onDrillDown }) {
  const [timePeriod, setTimePeriod] = useState('month');

  const timeData = useMemo(() => groupByPeriod(sales || [], timePeriod), [sales, timePeriod]);
  const metalData = useMemo(() => groupByMetal(sales || []), [sales]);
  const categoryData = useMemo(() => groupByCategory(sales || []), [sales]);

  const scatterData = useMemo(() => {
    return (sales || [])
      ?.filter(s => s?.yield_est && s?.actual_yield_pct)
      ?.map(s => ({
        x: parseFloat(s?.yield_est),
        y: parseFloat(s?.actual_yield_pct),
        metal: s?.metal_name,
      }));
  }, [sales]);

  const handleMetalBarClick = (data) => {
    if (data?.activePayload?.[0]?.payload?.metal) {
      onDrillDown?.({ type: 'metal', value: data?.activePayload?.[0]?.payload?.metal });
    }
  };

  const handleCategoryBarClick = (data) => {
    if (data?.activePayload?.[0]?.payload?.category) {
      onDrillDown?.({ type: 'category', value: data?.activePayload?.[0]?.payload?.category });
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {[1, 2, 3, 4]?.map(i => (
          <div key={i} className="bg-card border border-border rounded-xl p-4 h-64 animate-pulse">
            <div className="h-4 w-32 bg-muted rounded mb-4" />
            <div className="h-full bg-muted/50 rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {/* Line chart: margin over time */}
        <div className="bg-card border border-border rounded-xl p-4" style={{ boxShadow: 'var(--shadow-sm)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="TrendingUp" size={15} color="var(--color-accent)" strokeWidth={2} />
              <h3 className="text-sm font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Evolución del Margen</h3>
            </div>
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              {['week', 'month']?.map(p => (
                <button
                  key={p}
                  onClick={() => setTimePeriod(p)}
                  className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${
                    timePeriod === p ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
                >
                  {p === 'week' ? 'Semana' : 'Mes'}
                </button>
              ))}
            </div>
          </div>
          {timeData?.length === 0 ? (
            <div className="h-44 flex items-center justify-center text-xs text-muted-foreground">Sin datos suficientes</div>
          ) : (
            <ResponsiveContainer width="100%" height={176}>
              <LineChart data={timeData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="period" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Mono", monospace' }} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Mono", monospace' }} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="avgMargin" name="Margen" stroke="#059669" strokeWidth={2} dot={{ r: 3, fill: '#059669' }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Bar chart: by metal — clickable for drill-down */}
        <div className="bg-card border border-border rounded-xl p-4" style={{ boxShadow: 'var(--shadow-sm)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="BarChart2" size={15} color="var(--color-accent)" strokeWidth={2} />
              <h3 className="text-sm font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Margen Medio por Metal</h3>
            </div>
            {onDrillDown && (
              <span className="text-xs text-muted-foreground flex items-center gap-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                <Icon name="MousePointerClick" size={11} color="var(--color-muted-foreground)" />
                Clic para filtrar
              </span>
            )}
          </div>
          {metalData?.length === 0 ? (
            <div className="h-44 flex items-center justify-center text-xs text-muted-foreground">Sin datos suficientes</div>
          ) : (
            <ResponsiveContainer width="100%" height={176}>
              <BarChart data={metalData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }} onClick={handleMetalBarClick} style={{ cursor: onDrillDown ? 'pointer' : 'default' }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="metal" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Sans", sans-serif' }} />
                <YAxis tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Mono", monospace' }} />
                <Tooltip content={<CustomTooltip drillable={!!onDrillDown} />} />
                <Bar dataKey="avgMargin" name="Margen" radius={[3, 3, 0, 0]}>
                  {metalData?.map((_, i) => <Cell key={i} fill={METAL_COLORS?.[i % METAL_COLORS?.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Bar chart: by category — clickable for drill-down */}
        <div className="bg-card border border-border rounded-xl p-4" style={{ boxShadow: 'var(--shadow-sm)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Icon name="Layers" size={15} color="var(--color-accent)" strokeWidth={2} />
              <h3 className="text-sm font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Margen por Categoría (Top 10)</h3>
            </div>
            {onDrillDown && (
              <span className="text-xs text-muted-foreground flex items-center gap-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                <Icon name="MousePointerClick" size={11} color="var(--color-muted-foreground)" />
                Clic para filtrar
              </span>
            )}
          </div>
          {categoryData?.length === 0 ? (
            <div className="h-44 flex items-center justify-center text-xs text-muted-foreground">Sin datos suficientes</div>
          ) : (
            <ResponsiveContainer width="100%" height={176}>
              <BarChart data={categoryData} layout="vertical" margin={{ top: 4, right: 8, bottom: 4, left: 80 }} onClick={handleCategoryBarClick} style={{ cursor: onDrillDown ? 'pointer' : 'default' }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Mono", monospace' }} />
                <YAxis type="category" dataKey="category" tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Sans", sans-serif' }} width={80} />
                <Tooltip content={<CustomTooltip drillable={!!onDrillDown} />} />
                <Bar dataKey="avgMargin" name="Margen" radius={[0, 3, 3, 0]}>
                  {categoryData?.map((_, i) => <Cell key={i} fill={METAL_COLORS?.[i % METAL_COLORS?.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Scatter: yield est vs real */}
        <div className="bg-card border border-border rounded-xl p-4" style={{ boxShadow: 'var(--shadow-sm)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Icon name="Crosshair" size={15} color="var(--color-accent)" strokeWidth={2} />
            <h3 className="text-sm font-semibold text-foreground" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Yield Estimado vs Real</h3>
          </div>
          {scatterData?.length === 0 ? (
            <div className="h-44 flex items-center justify-center text-xs text-muted-foreground">Sin datos de yield disponibles</div>
          ) : (
            <ResponsiveContainer width="100%" height={176}>
              <ScatterChart margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis dataKey="x" name="Yield Est." type="number" domain={['auto', 'auto']} tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Mono", monospace' }} label={{ value: 'Est. %', position: 'insideBottomRight', offset: -5, fontSize: 10 }} />
                <YAxis dataKey="y" name="Yield Real" type="number" domain={['auto', 'auto']} tick={{ fontSize: 10, fill: 'var(--color-muted-foreground)', fontFamily: '"IBM Plex Mono", monospace' }} label={{ value: 'Real %', angle: -90, position: 'insideLeft', fontSize: 10 }} />
                <ReferenceLine stroke="#94a3b8" strokeDasharray="4 4" segment={[{ x: 70, y: 70 }, { x: 100, y: 100 }]} />
                <Tooltip cursor={{ strokeDasharray: '3 3' }} content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload?.[0]?.payload;
                  return (
                    <div className="bg-card border border-border rounded-lg p-2.5 shadow-lg" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                      <p className="text-xs font-medium text-foreground">{d?.metal}</p>
                      <p className="text-xs text-muted-foreground">Est: {d?.x?.toFixed(1)}% → Real: {d?.y?.toFixed(1)}%</p>
                      <p className={`text-xs font-semibold ${d?.y - d?.x >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                        Δ {(d?.y - d?.x)?.toFixed(1)}pp
                      </p>
                    </div>
                  );
                }} />
                <Scatter data={scatterData} fill="#2563eb" opacity={0.7} />
              </ScatterChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
